import React, { useState, useEffect, useRef } from "react";
import Axios, { CancelTokenSource } from "axios";
import { format } from "date-fns";
import { Avatar } from "../../components/avatar/index";
import { useHistory, RouteChildrenProps } from "react-router-dom";
import Sidebar from "./components/Sidebar";
import QueryString from "query-string";
import { SearchInput } from "components/input";
import { Button } from "components/button";
import { useModalContext } from "components/modal";
import { useQuery, useMutation } from "react-query";
import { ContainerWithSidebar2 } from "components/layout";
import Table, { useTable } from "components/table";
import { useTranslation } from 'react-i18next';
import { useAuthContext } from "contexts/auth";
import { debounce } from "lodash";
import ServicePlanModal from "./components/ServicePlanModal";
import { IQuery, IServicePlan, IFilter, IStatusCount, ISelectedPlan } from "./interface";
import convertCVD from "utils/convertCVD";
import { Status } from "components/status";
import SendPlansModal from "./components/SendPlansModal";
import Switch from "rc-switch";
import TooltippedElement from "components/TooltippedElement";
import ResendPlansModal from "./components/ResendPlansModal";
import Dropdown from "components/dropdown";
import MinimalPlans from "./components/MinimalPlans";
import SelectedPlanSidebar from "./components/SelectedPlanSidebar";
import ArchiveOrDeleteModal from "./components/ArchiveOrDeleteModal";


// Icons
import { IoMdAdd } from "react-icons/io";
import { IoChevronForwardOutline, IoChevronBackOutline, IoClose, IoCloseCircleOutline } from "react-icons/io5";
import { RiDeleteBin2Line, RiAddLine, RiArrowDownSLine, RiDeleteBin7Line, RiCheckboxMultipleLine } from "react-icons/ri";
import { LiaMailBulkSolid } from "react-icons/lia";
import { FaRedo } from "react-icons/fa";
import { LuArchive } from "react-icons/lu";
import Spinner from "components/spinner";


const PAGE_SIZE = 20;
const CHUNK_SIZE = 100; 
const MAX_CONCURRENT_REQUESTS = 5; // Limit concurrent API calls

const ServicePlanDashboard = React.memo(function ServicePlanDashboard(props: RouteChildrenProps) {
  const { t } = useTranslation();
  const tBase = "views.service-plan-dashboard.index";
  const tr = (key: string) => t(`${tBase}.${key}`);

  const { location } = props;
  const { cvd_type } = useAuthContext();
  const { setModal } = useModalContext();
  const history = useHistory();
  const table = useTable({
    defaultSort: {
      column: "customer_name",
      order: "ASC"
    }
  });

  const [initialLoad, setInitialLoad] = useState(true);
  const [query, setFilter] = useState<IQuery>({});
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const tableRef = useRef<any>();
  const [showSidebar, setShowSidebar] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState<IFilter[]>([]);
  const [selected, setSelected] = useState<string>("");
  const [exactMatch, setExactMatch] = useState(false);
  const [selectedPlans, setSelectedPlans] = useState<any[]>([]);
  const [showSelectedPlans, setShowSelectedPlans] = useState(false);
  const [isExpiring, setIsExpiring] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [showArchivedOnly, setShowArchivedOnly] = useState(false);
  const [selectAllProgress, setSelectAllProgress] = useState("");
  const [isSelectingAll, setIsSelectingAll] = useState(false);
  const [cancelTokenSource, setCancelTokenSource] = useState<CancelTokenSource | null>(null);
  const [buttonSelection, setButtonSelection] = useState<string>("");

  useEffect(() => {
    const rawString = QueryString.parse(location.search);
    const parsedString: { [key: string]: string[] } = {};

    Object.entries(rawString).forEach(([key, value]) => {
      if (typeof value === "string") {
        parsedString[key] = value.split("|");
      }
    });

    const queryPage = parsedString.page ? parseInt(parsedString.page[0]) : 1;

    if (queryPage !== table.pageIndex) {
      table.handlePage(queryPage); 
    }

    setFilter(parsedString);

    setInitialLoad(false);
  }, [location.search]);

  useEffect(() => {
    if (!initialLoad) {
      table.handlePage(1)
    }
  }, [selected, isExpiring])

  useEffect(() => {
    !initialLoad && updateUrl({ ...query, page: [table.pageIndex.toString()] });
  }, [initialLoad, table.pageIndex]);

  const updateUrl = (query: IQuery) => {
    let stringifyQuery: { [key: string]: string } = {};

    Object.entries(query).forEach(([key, value]) => {
      stringifyQuery[key] = value.join("|");
    });

    history.push({
      search: QueryString.stringify(stringifyQuery),
    });
  };

  const debouncedSetSearch = useRef(
    debounce((inputValue: string) => {
      updateUrl({ ...query, page: ["1"] });
      setDebouncedSearch(inputValue);
    }, 200)
  ).current;

  const handleDebounce = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const inputValue = event.target.value;
    setSearch(inputValue);
    debouncedSetSearch(inputValue);
  };

  const { mutateAsync: fetchPlanChunk } = useMutation(
    async ({ page, perPage, cancelToken }: { page: number; perPage: number; cancelToken?: CancelTokenSource }) => {
      const serializedFilters = filters.map(filter => ({
        ...filter,
        input: filter.value === "date_range" 
          ? JSON.stringify(filter.input) 
          : filter.input
      }));

      const { data } = await Axios.get<{ items: IServicePlan[] }>(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/service-plans/select-data`,
        {
          params: {
            search: debouncedSearch.trim(),
            page,
            perPage,
            filters: serializedFilters.length > 0 ? JSON.stringify(serializedFilters) : [],
            selected,
            exactMatch: exactMatch.toString(),
            isExpiring: isExpiring.toString(),
            showArchived: showArchived.toString(),
            showArchivedOnly: showArchivedOnly.toString(),
          },
          cancelToken: cancelToken?.token // Use cancel token
        }
      );
      return data.items;
    }
  );

  // For selecting ALL plans in chunks on column
  const handleSelectAll = async () => {
    if (!data?.total) return;
    
    // Cancel any existing operation
    if (cancelTokenSource) {
      cancelTokenSource.cancel("New selection started");
    }
    
    const newCancelSource = Axios.CancelToken.source();
    setCancelTokenSource(newCancelSource);
    setIsSelectingAll(true);
    
    try {
      const totalCount = data.total;
      const totalChunks = Math.ceil(totalCount / CHUNK_SIZE);
      let allPlans: IServicePlan[] = [];
      
      // Process chunks in batches
      for (let batchStart = 0; batchStart < totalChunks; batchStart += MAX_CONCURRENT_REQUESTS) {
        const batchEnd = Math.min(batchStart + MAX_CONCURRENT_REQUESTS, totalChunks);
        const batchPromises = [];
        
        // Create promises for current batch
        for (let chunkIndex = batchStart; chunkIndex < batchEnd; chunkIndex++) {
          const page = chunkIndex + 1;
          batchPromises.push(
            fetchPlanChunk({ 
              page, 
              perPage: CHUNK_SIZE,
              cancelToken: newCancelSource
            })
          );
        }
        
        // Execute batch concurrently
        const batchResults = await Promise.all(batchPromises);
        
        // Add all results from this batch
        batchResults.forEach(chunkPlans => {
          allPlans = [...allPlans, ...chunkPlans];
        });
      }

      // Filter plans based on selection type
      let plansToSelect = allPlans;
      if (buttonSelection === "delete") {
        // Only select archived plans for deletion
        plansToSelect = allPlans.filter(plan => plan.archived);
      }

      // Filter out already selected plans and add new ones
      const newPlans = plansToSelect.filter(plan => 
        !selectedPlans.some(selected => selected.sptc_id === plan.sptc_id)
      );
      
      // setSelectedPlans(prev => [...prev, ...newPlans]);
      setSelectedPlans(plansToSelect);
      
    } catch (error) {
      if (Axios.isCancel(error)) {
        setSelectAllProgress("Selection cancelled");
      } else {
        console.error('Error selecting all plans:', error);
        setSelectAllProgress("Error loading plans");
      }
      setTimeout(() => setSelectAllProgress(""), 3000);
    } finally {
      setIsSelectingAll(false);
      setCancelTokenSource(null);
    }
  };
  
  // Function to cancel ongoing selection
  const cancelSelectAll = () => {
    if (cancelTokenSource) {
      cancelTokenSource.cancel("User cancelled selection");
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (cancelTokenSource) {
        cancelTokenSource.cancel("Component unmounted");
      }
    };
  }, []);

  // Clears selected plans when any of the filtering/searching changes
  useEffect(() => {
    setSelectedPlans([]);
  }, [debouncedSearch, table.sort?.column, table.sort?.order, filters, selected, exactMatch, isExpiring, showArchived, showArchivedOnly]);

  const { data } = useQuery(
    ["servicePlans", debouncedSearch, table.pageIndex, table.sort?.column, table.sort?.order, filters, selected, exactMatch, isExpiring, showArchived, showArchivedOnly],
    async () => {
      const serializedFilters = filters.map(filter => ({
        ...filter,
        input: filter.value === "date_range" 
          ? JSON.stringify(filter.input) 
          : filter.input
      }));

      const { data } = await Axios.get<{
        items: IServicePlan[];
        total: number;
        statusCount: IStatusCount[];
      }>(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/service-plans`,
        {
          params: {
            search: debouncedSearch.trim(),
            page: table.pageIndex,
            perPage: PAGE_SIZE,
            sortColumn: table.sort?.column,
            sortOrder: table.sort?.order,
            filters: serializedFilters.length > 0 ? JSON.stringify(serializedFilters) : [],
            selected,
            exactMatch: exactMatch.toString(),
            isExpiring: isExpiring.toString(),
            showArchived: showArchived.toString(),
            showArchivedOnly: showArchivedOnly.toString(),
          }
        }
      );
      return data;
    },
    {
      enabled: !initialLoad,       
      onSuccess: (data) => {
        tableRef.current?.scrollTo(0, 0);
        // Calculate total pages from total items and page size
        const calculatedTotalPages = Math.ceil(data?.total / PAGE_SIZE);
        setTotalPages(calculatedTotalPages);
        table.setPageCount(calculatedTotalPages);
      },
    }
  );

  const { mutateAsync: getPdfContract } = useMutation(async (info: { servicePlanId: string; customerId: string }) => {
    return Axios
      .get(`${process.env.REACT_APP_SERVER_URL}/api/v1/service-plans/file`,
        { params: info }
      )
      .then((res) => {
        return res.data;
      });
  });

  const columns = {
    customer_name: {
      label: tr("Customer"),
      cell: (row: any) => (
        <Avatar size="small" showLabel>
          {row.customer_name}
        </Avatar>
      ),
    },
    plan_name: {
      label: tr("Plan"),
    },
    status: {
      label: tr("Status"),
      cell: (row: any) => {
        const statusColor = convertCVD(row.status.color, cvd_type);
        if (!row.status.label) return null;

        return (
          <div className="flex items-center gap-2">
            {row.status.label === "Failed" && row.failed_note ? (
              <TooltippedElement
                message={row.failed_note}
                placement="top"
                element={<div className="mb-[2px]"><Status color={statusColor}>{tr(row.status.label)}</Status></div>}
              />
            ) : (
              <Status color={statusColor}>{tr(row.status.label)}</Status>
            )}
            {row.expiring_soon && row.status.id !== "46" && (
              <TooltippedElement
                message={tr("Expiring Soon")}
                placement="top"
                element={<span className="font-bold text-red-500">!!!</span>}
              />
            )} 
          </div>
        )
      },
    },
    description: {
      label: tr("Description"),
      cell: (row: IServicePlan) => (
        <div className="max-w-[300px]" title={row.description}>
          <div className="truncate">{row.description || "-"}</div>
        </div>
      ),
    },
    property: {
      label: tr("Property"),
      cell: (row: IServicePlan) => {
        const address = row.property;
        return (
          <div className="max-w-[300px] truncate" title={address.address}>
            {address.address} {address.city && `, ${address.city}`} {address.state && `, ${address.state}`} {address.zipcode && `, ${address.zipcode}`}
          </div>
        );
      },
    },
    approval_date: {
      label: tr("Approval Date"),
      cell: (row: IServicePlan) =>
        row.approval_date
          ? format(new Date(row.approval_date), "MMM dd, yyyy")
          : "----",
    },
    start_date: {
      label: tr("Start Date"),
      cell: (row: IServicePlan) =>
        row.start_date
          ? format(new Date(row.start_date), "MMM dd, yyyy")
          : "----",
    },
    duration: {
      label: "",
      labelDiv: (
        <>
          <div>{tr("Duration")}</div>
          <div className="text-xs text-gray-500">({tr("Months")})</div>
        </>
      )
    },
  };

  useEffect(() => {
    if (buttonSelection === "delete") {
      setSelectedPlans([]);
    }
  }, [buttonSelection]);

  // Select column for checkboxes for selecting plans
  const selectCol = {
    select: {
      label: "",
      disableRowClick: true,
      disableSort: true,
      labelDiv: (
        <div className="flex items-center relative">
          <input
            type="checkbox"
            checked={
              selectedPlans.length > 0 && 
              (buttonSelection === "delete" 
                ? true
                : selectedPlans.length === data?.total
              )
            }
            onChange={async (e) => {
              e.stopPropagation();
              if (e.target.checked) {
                await handleSelectAll();
              } else {
                cancelSelectAll();
                setSelectedPlans([]);
                setSelectAllProgress("");
              }
            }}
            disabled={isSelectingAll || (buttonSelection === "delete" && !data?.items.some(plan => plan.archived))}
          />
          {isSelectingAll && (
            <Spinner className="absolute top-0 right-0 text-primary z-[2] h-[13px] w-[13px]" color="#ff4f00"/>
          )}
        </div>
      ),
      cell: (row: IServicePlan) => {
        const isDisabled = buttonSelection === "delete" && !row.archived;

        return (
          <div className="flex items-center justify-center">
            <input
              type="checkbox"
              checked={selectedPlans.some(plan => plan.sptc_id === row.sptc_id)}
              onChange={(e) => {
                e.stopPropagation();
                const checked = e.target.checked;
                if (checked) {
                  setSelectedPlans([...selectedPlans, row]);
                } else {
                  setSelectedPlans(selectedPlans.filter(plan => plan.sptc_id !== row.sptc_id));
                }
              }}
              disabled={isSelectingAll || isDisabled}
            />
          </div>
        );
      }
    },
  }

  // Adds select column to beginning of columns if selectActive is true
  let combinedColumns = {};
  if (buttonSelection !== "") {
    combinedColumns = {
      ...selectCol,
      ...columns,
    };
  } else {
    combinedColumns = {
      ...columns,
    };
  };

  return (
    <ContainerWithSidebar2 className="relative">
      {!showSidebar && (
        <button
          className="fixed p-0.5 h-6 w-6 z-20 top-[55px] left-[5px] text-primary border-2 border-primary bg-white rounded-full hover:bg-primary hover:text-white"
          onClick={() => setShowSidebar(true)}
        >
          <IoChevronForwardOutline size={18} />
        </button>
      )}
      {showSidebar && (
        <Sidebar
          counts={data?.statusCount}
          setTotalPages={setTotalPages}
          {...{
            showSidebar,
            setShowSidebar,
            filters,
            setFilters,
            selected,
            setSelected,
            exactMatch,
            setExactMatch,
            isExpiring,
            setIsExpiring,
          }}
        />
      )}

      <div className="p-8 relative">

        {/* Button for selected plans Sidebar */}
        {buttonSelection !== "" && !showSelectedPlans && (
          <>
            <button
              className="fixed p-0.5 z-10 top-[55px] right-[2.5px] text-primary border-2 border-primary bg-white rounded-full hover:bg-primary hover:text-white"
              onClick={() => setShowSelectedPlans(true)}
            >
              <IoChevronBackOutline size={18} />
            </button>
            <div className={`
              fixed flex items-center justify-center w-[25.2px] h-[25.2px] z-10 top-[85px] right-[2.5px] text-white border-2 border-secondary bg-secondary rounded-full
              ${selectedPlans.length > 999 ? " text-[10px]" : " text-xs"}
            `}>
              {selectedPlans.length}
            </div>
          </>
        )}

        {/* Sidebar for selected plans */}
        {showSelectedPlans && (
          <SelectedPlanSidebar
            setShowSelectedPlans={setShowSelectedPlans}
            setSelectedPlans={setSelectedPlans}
            selectedPlans={selectedPlans}
          />
        )}

        <div className="flex items-center mb-4">
          <div className="flex items-center justify-between flex-1">
            <div className="flex items-center space-x-4">
              <h2>{tr("Service Plans")}</h2>
              <SearchInput
                search
                placeholder={tr("Search...")}
                style={{ width: "400px" }}
                value={search}
                onChange={handleDebounce}
              />
              <div className="flex-wrap flex items-center gap-1">
                <div className="text-sm text-gray-700">{tr("Show Archived")}</div>
                <Switch 
                  checked={showArchived}
                  onChange={() => {
                    setShowArchived(!showArchived)
                    setShowArchivedOnly(false) // Reset archived only when toggling
                  }}
                />
              </div>
              {showArchived && (
                <div className="flex-wrap flex items-center gap-1">
                  <div className="text-sm text-gray-700">{tr("Show Archived Only")}</div>
                  <Switch 
                    checked={showArchivedOnly}
                    onChange={() => setShowArchivedOnly(!showArchivedOnly)}
                  />
                </div>
              )}
            </div>
            <div className="flex items-center justify-end space-x-4">
              {/* Multi Action button */}
              {buttonSelection !== "" && (
                <>
                  <Button
                    primary
                    disabled={selectedPlans.length === 0}
                    onClick={() => {
                      if (buttonSelection === "send") {
                        setModal({
                          label: tr("Send Plans"),
                          component: (
                            <SendPlansModal 
                              key={selectedPlans.length} // Reset key to re-render modal on new selection
                              selectedPlans={selectedPlans} 
                              setSelectedPlans={setSelectedPlans}
                              setModal={setModal} 
                              setButtonSelection={setButtonSelection}
                            />
                          ),
                        });
                        return;
                      } else if (buttonSelection === "archive" || buttonSelection === "delete") {
                        setModal({
                          label: buttonSelection === "archive" ? tr("Archive Plans") : tr("Delete Plans"),
                          component: (                        
                            <ArchiveOrDeleteModal
                              key={selectedPlans.length} // Reset key to re-render modal on new selection
                              selectedPlans={selectedPlans}
                              setSelectedPlans={setSelectedPlans}
                              buttonSelection={buttonSelection}
                              setButtonSelection={setButtonSelection}
                              setShowSelectedPlans={setShowSelectedPlans}
                              setModal={setModal}
                            />
                          )
                        })

                      }
                    }}
                  >
                    {buttonSelection === "send" 
                      ? tr("Send") 
                    : buttonSelection === "delete"
                      ? tr("Delete")
                    : buttonSelection === "archive"
                      ? tr("Archive")
                      : ""}
                    {tr(" Selected Plans")} ({selectedPlans.length})
                  </Button>
                  <Button 
                    onClick={() => {
                      setButtonSelection("");
                      setSelectedPlans([]);
                    }}
                  >
                    {tr("Cancel")}
                  </Button>
              </>
              )}
              <Dropdown
                reference={
                  <Button primary>
                    <RiArrowDownSLine /> {tr("Bulk Actions")}
                  </Button>
                }
                data={[
                  {
                    button: [
                      {
                        label: tr("Bulk Send"),
                        icon: <LiaMailBulkSolid size={20} className="text-green-600" />,
                        tooltip: tr("Send plans in bulk to customers"),
                        onClick: () => history.push("/service-plan-dashboard/bulk-send")
                      },
                      {
                        label: tr("Select to Send"),
                        icon: <RiCheckboxMultipleLine size={20} className="text-secondary" />,
                        tooltip: tr("Select plans to send or resend"),
                        onClick: () => setButtonSelection("send")
                      },
                      // {
                      //   label: tr("Resend Plans"),
                      //   icon: <FaRedo size={16} className="text-[#f4ae3a] mr-[4px]" />,
                      //   tooltip: tr("Resend pending plans"),
                      //   onClick: () => {
                      //     setModal({
                      //       label: tr("Resend Plans"),
                      //       component: (
                      //         <ResendPlansModal />
                      //       ),
                      //     });
                      //   }
                      // },
                      {
                        // label: tr("Delete"),
                        label: (
                          <>
                            <div>
                              <span>{tr("Delete")}</span>
                              <span className="text-xs text-gray-500"> ({tr("Archived Only")})</span>
                            </div>
                          </>
                        ),
                        icon: <RiDeleteBin2Line size={20} className="text-red-500" />,
                        tooltip: tr("Select plans to delete"),
                        onClick: () => setButtonSelection("delete"),
                      },
                      {
                        label: tr("Archive"),
                        icon: <LuArchive size={20} className="text-purple-500" />,
                        tooltip: tr("Select plans to archive"),
                        onClick: () => setButtonSelection("archive"),
                      }
                    ],
                  },
                ]}
              />
            </div>
          </div>
        </div>
        <Table
          contentType="service-plans"
          {...{ ...table, columns: combinedColumns }}
          data={data?.items}
          pageCount={totalPages}
          pageIndex={table.pageIndex}
          sort={table.sort}
          handlePage={(page) => table.handlePage(page)}
          handleClick={(row: IServicePlan) => {
            setModal({
              label: tr("Service Plan"),
              component: (
                <ServicePlanModal
                  data={row}
                  onClose={() => setModal()}
                  getPdfContract={getPdfContract}
                />
              ),
            });
          }}
        />
      </div>
    </ContainerWithSidebar2>
  );
});

export default ServicePlanDashboard;